<template>
  <component
    :is="component"
    v-if="isNotEmptySection"
    :style="sectionStyle"
  >
    <template
      v-for="(_, name) in $scopedSlots"
      #[name]
    >
      <slot :name="name"></slot>
    </template>
  </component>
</template>
<script>
  import EventEmitter from '@atds-legacy/events';
  import { SectionContentType, SectionLayout } from '@/shared/enums';
  import { Products } from '@/shared/libs/sdk';
  import { ADULT_RATING_AGE, convertToBoolean, isAdult, updateUrlParams } from '@/shared/utils';
  import layouts from './layouts';

  export default {
    provide() {
      return {
        $section: this,
      };
    },
    props: {
      initialFocusIndex: Number,
      header: String,
      layout: String,
      resource: [Object, Array],
      progress: Boolean,
      showHighlight: Boolean,
    },
    data() {
      return {
        subscriber: this.$services.resolve('subscriber'),
        favourites: this.$services.resolve('favourites'),
        bookmarks: this.$services.resolve('bookmarks'),
        bus: new EventEmitter(),
        products: [],
      };
    },
    computed: {
      component() {
        return layouts[this.sectionLayout];
      },
      isBannerRecommendation() {
        return this.sectionLayout === SectionLayout.BANNER && this.resource.contentType === SectionContentType.RECOMMENDATION;
      },
      isMultipleHero() {
        return this.baseSectionLayout === SectionLayout.HERO && this.products.length >= 4;
      },
      isAdSection() {
        return !!this.resource?.configuration?.adSystem;
      },
      isNotEmptySection() {
        return this.products.length || this.isBannerRecommendation || this.isAdSection;
      },
      sectionStyle() {
        return {
          background: this.resource.background,
          color: this.resource.color,
        };
      },
      resourceLayout() {
        if (this.resource.contentType === SectionContentType.CUSTOM) {
          return this.resource.configuration.layout?.toUpperCase() || this.resource.layout;
        }
        return this.resource.layout;
      },
      baseSectionLayout() {
        return this.layout || this.resourceLayout || SectionLayout.DEFAULT;
      },
      sectionLayout() {
        if (this.isMultipleHero) {
          return this.$window.isMobileView ? SectionLayout.HERO_SLIDER : SectionLayout.HERO_MULTIPLE;
        }

        return this.baseSectionLayout;
      },
      sectionHeader() {
        return this.header || this.resource.title;
      },
      tileProgress() {
        return this.progress || this.resource.contentType !== SectionContentType.STATIC;
      },
      isUnknownAge() {
        return !this.subscriber.dateOfBirth;
      },
      isUpsellSection() {
        return Boolean(this.resource?.configuration?.contentFeed);
      },
    },
    watch: {
      resource: 'load',
    },
    mounted() {
      this.load();
    },
    methods: {
      load() {
        if (!this.resource) {
          return;
        }

        if (this.resource instanceof Array) {
          this.products = this.filterProducts(this.resource);
          return;
        }

        this.resource.on('sync', this.init);
        this.init();
      },
      async init() {
        if (this.resource.contentType === SectionContentType.SUBSCRIBER_WATCHED) {
          if (this.subscriber.isLogged) {
            const continueWatching = this.bookmarks.continueWatching;
            this.products = this.filterProducts(continueWatching);
          }
          return;
        }

        if (this.resource.contentType === SectionContentType.SUBSCRIBER_FAVOURITE) {
          if (!this.subscriber.isLogged) {
            return;
          }

          const favourites = this.favourites.collection.records;
          this.products = this.filterProducts(favourites);
          return;
        }

        if (this.resource.contentType === SectionContentType.CUSTOM) {
          const isExternalView = convertToBoolean(this.resource.configuration?.externalView);
          const utmParams = this.resource.configuration?.utmParams;

          const products = this.isUpsellSection ? await new Products().fetchAll({ portal: 'SVOD' }, { contentFeed: this.resource.configuration.contentFeed })
            : this.resource.configuration.elements || this.resource.elements.collection.records;

          this.products = this.filterProducts(products);
          this.products.forEach((product) => {
            product.externalView = isExternalView;

            if (product.urlWeb && utmParams) {
              product.urlWeb = updateUrlParams(product.urlWeb, utmParams);
            }
          });
          return;
        }

        const products = this.resource.collection?.records || this.resource.elements.collection.records;
        this.products = this.filterProducts(products);
      },

      filterProducts(products) {
        return this.filterOutAdultContent(products);
      },
      filterOutAdultContent(products) {
        if (this.isUnknownAge) {
          return products;
        }

        return products.filter((product) => {
          const rating = product.rating;
          return !rating || rating < ADULT_RATING_AGE || isAdult(this.subscriber.dateOfBirth);
        });
      },
    },
  };
</script>
